import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { idleHostSummary, idleMoney, idleText } from '@evi/shared';
import { renderInline } from '@evi/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Loader2, ExternalLink, Check, RefreshCw } from 'lucide-react';
import {
  fetchMyCharger,
  updateMyCharger,
  updateHomeGuestRate,
  setMyHomeRate,
  enrollHomeMonetization,
  ackResidentDisclaimer,
  startNewPayoutAccount,
  fetchConnectStatus,
  type MyChargerDetail,
} from '@/lib/api';
// The idle-fee bounds, from the same shared rule the server rejects
// with — so the walk refuses exactly what the guard would.
import {
  HOME_IDLE_RATE_MIN_CENTS, HOME_IDLE_RATE_MAX_CENTS, IDLE_FEE_MAX_MINUTES,
  IDLE_GRACE_DEFAULT_MINUTES, IDLE_GRACE_MAX_MINUTES, IDLE_GRACE_MIN_MINUTES, isValidIdleGraceMinutes,
} from '@evi/shared';
import {
  computeHostMargin, hostMarginText, parseDollarsToCents, hostKeepCents,
  type UtilityRateEstimate,
} from '@/lib/hostMargin';
import { moneyFormat } from '@/lib/money';
import { idleDisclosure } from '@/lib/idleDisclosure';
import { SUGGESTED_GUEST_RATE_CENTS } from '@/components/home/EarnPromptCard';
import {
  homeRateConfirmCents, isUtilityRateOverCeiling, utilityCeilingInDollars, utilityCeilingLabel, utilityDollarsToCents,
} from '@/lib/utilityRateCeiling';
import { HomeRateConfirmStep } from '@/components/home/HomeRateConfirmStep';
import { FormError, useFormError } from '@/components/ui/FormError';
import { TimeWindowRows } from '@/components/ui/TimeWindowRows';
import { DisclaimerModal } from '@/components/ui/DisclaimerModal';
import { ConnectStatusCard, connectCardState } from '@/components/connect/ConnectStatusCard';
import { PayoutCountryField } from '@/components/connect/PayoutCountryField';
import {
  apiTakesCountry, canStartPayout, isCountryRequiredCode, payoutCountryFrom, payoutCountryTextKey,
  type PayoutCountry,
} from '@/lib/payoutCountry';
import { useAuthStore } from '@/store/auth';
import { toast } from '@/store/toast';
import formStyles from '@/components/Form.module.css';
import styles from './EarnWalkPage.module.css';

interface Props {
  chargerId: string;
  onNavigate: (path: string) => void;
}

type Step = 'price' | 'hours' | 'paid';
const STEPS: readonly Step[] = ['price', 'hours', 'paid'];

/** Same bounds the guest-rate route enforces (homeGuestTariff.ts). */
const GUEST_RATE_MIN_CENTS = 1;
const GUEST_RATE_MAX_CENTS = 200;

/** How long the Stripe button stays disabled if the tab never loses
 *  visibility (the link opened in the same tab and the page is about
 *  to unload anyway, or the resident dismissed the external browser
 *  without it ever reporting back). */
const STRIPE_BUTTON_RELEASE_MS = 5 * 60_000;

interface GuestTimeWindowState {
  start_hour: number;
  end_hour: number;
  days_of_week: number[];
  /** Client-only React key. Never sent — the request body picks the
   *  three wire fields explicitly, and the server schema is
   *  additionalProperties:false anyway. Same reasoning as the charger
   *  Settings form: rows have no server id and no naturally unique
   *  content, so a minted id is the only stable key. */
  _key: string;
}

/** Monotonic row key — a counter, not a random id, so it stays stable
 *  under React StrictMode's double-invoke. */
let windowKeySeq = 0;
const nextWindowKey = (): string => `w${(windowKeySeq += 1)}`;

const seedWindows = (
  saved: ReadonlyArray<{ start_hour: number; end_hour: number; days_of_week: number[] }>,
): GuestTimeWindowState[] => saved.map((w) => ({
  ...w,
  days_of_week: [...w.days_of_week],
  _key: nextWindowKey(),
}));

/**
 * The price draft, held client-side until enrolment.
 *
 * PATCH /me/home/guest-rate answers 402 OPERATOR_NOT_CONFIGURED until
 * the home tenant carries the owner's Express account — which is what
 * POST /me/home/monetization/enroll stamps. So for the walk's whole
 * audience (unenrolled homes) the price CANNOT be persisted at step 1;
 * it is written the moment enrolment succeeds at step 3. In between it
 * lives here, and in localStorage keyed by charger so the Stripe
 * round-trip (a full navigation away and back) does not lose it.
 * Cleared once the server has it.
 *
 * No fee but the idle fee: the connection fee is EVI's (Tier 2), set
 * per charger by EVI, and the host cannot set it. A draft saved before
 * that change may still hold a `plugInFeeStr`; readDraft never reads it
 * and the next write drops it, so a stale draft can neither show nor
 * send a host fee.
 */
interface EarnDraft {
  guestRateStr: string;
  idleFeeStr: string;
  /** The idle fee's grace, minutes (2026-09-28). Absent in a draft saved
   *  before the field: read back as the default. */
  idleGraceStr?: string;
  idleLimitStr?: string;
}
/** True when the draft for this charger was written during this app
 *  open (it is in the memo, not only in storage). A draft that only
 *  survives in localStorage is from an earlier visit — still worth
 *  resuming from, but never worth overwriting a price the server has
 *  since been given. */
function draftIsFresh(chargerId: string): boolean {
  return draftMemo.has(chargerId);
}
const draftKey = (chargerId: string): string => `evi-earn-draft:${chargerId}`;
/** In-memory mirror of the draft: localStorage can be unavailable
 *  (private mode, storage cleared), and within one app open the price
 *  typed at step 1 must still reach step 3 regardless. */
const draftMemo = new Map<string, EarnDraft>();
function readDraft(chargerId: string): EarnDraft | null {
  const inMemory = draftMemo.get(chargerId);
  if (inMemory) return inMemory;
  try {
    const raw = window.localStorage.getItem(draftKey(chargerId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<EarnDraft>;
    // Only the two known fields are picked: anything else in an old
    // draft (a pre-Tier-2 plugInFeeStr) is dropped here.
    return {
      guestRateStr: typeof parsed.guestRateStr === 'string' ? parsed.guestRateStr : '',
      idleFeeStr: typeof parsed.idleFeeStr === 'string' ? parsed.idleFeeStr : '',
      idleGraceStr: typeof parsed.idleGraceStr === 'string' ? parsed.idleGraceStr : undefined,
      idleLimitStr: typeof parsed.idleLimitStr === 'string' ? parsed.idleLimitStr : undefined,
    };
  } catch {
    return null;
  }
}
function writeDraft(chargerId: string, draft: EarnDraft): void {
  draftMemo.set(chargerId, draft);
  try { window.localStorage.setItem(draftKey(chargerId), JSON.stringify(draft)); } catch { /* private mode */ }
}
function clearDraft(chargerId: string): void {
  draftMemo.delete(chargerId);
  try { window.localStorage.removeItem(draftKey(chargerId)); } catch { /* private mode */ }
}

/** ?step=price|hours|paid — the connect/complete landing sends the
 *  resident back to the Get paid step with `?step=paid`. */
function readStepParam(): Step | null {
  const raw = new URLSearchParams(window.location.search).get('step');
  return raw === 'price' || raw === 'hours' || raw === 'paid' ? raw : null;
}

/** A guest price in cents the server would accept, or null. */
function validGuestCents(s: string): number | null {
  const c = parseDollarsToCents(s);
  return c != null && c >= GUEST_RATE_MIN_CENTS && c <= GUEST_RATE_MAX_CENTS ? c : null;
}

/**
 * /my-chargers/:id/earn — the earn walk (frictionless flow, piece 3).
 *
 * Three steps for a home charger the driver owns: Price → Hours → Get
 * paid, then the enrol call. Every control is in flow; the page's
 * bottom padding clears the pill nav. The walk never dead-ends on a
 * server gate: the disclaimer opens inline, the Stripe state is the
 * step itself, and payout terms are handled by the api helper.
 *
 * Call order is dictated by the existing routes' gates, not by the
 * screen order — see EarnDraft. Hours (PATCH my-chargers/:id
 * guest_time_windows) and the electricity estimate (PATCH
 * /me/home/rate) have no enrolment gate and save where they are typed;
 * the price saves right after enrol succeeds.
 */
export function EarnWalkPage({ chargerId, onNavigate }: Props) {
  const { t } = useTranslation('earn');
  const qc = useQueryClient();
  const chargerPath = `/my-chargers/${chargerId}`;
  const { data: charger, isLoading, error } = useQuery({
    queryKey: ['my-charger', chargerId],
    queryFn: () => fetchMyCharger(chargerId),
  });

  // Set the moment enrolment lands. The charger refetch below flips
  // home_monetization.enrolled, which would otherwise swap the walk for
  // the "already earning" note for a frame before the navigate.
  const [leaving, setLeaving] = useState(false);
  const handleEnrolled = async (): Promise<void> => {
    setLeaving(true);
    // Awaited so the detail page mounts already unlocked, not on the
    // cached unenrolled doc with a refetch in flight.
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['my-charger', chargerId] }),
      qc.invalidateQueries({ queryKey: ['my-chargers'] }),
    ]);
    toast(t('toast.enrolled'), { tone: 'accent' });
    onNavigate(chargerPath);
  };

  const home = charger?.home_monetization;
  // Not a home charger, or already earning: a short note and the way
  // back. The detail page owns price / hours edits after enrolment.
  const gateNote = charger && !leaving
    ? (home == null ? 'notHome' : home.enrolled ? 'alreadyEnrolled' : null)
    : null;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <button
          type="button"
          className={styles.backFab}
          aria-label={t('back')}
          onClick={() => onNavigate(chargerPath)}
        >
          <ArrowLeft size={16} />
        </button>
        <div className={styles.titleBlock}>
          <h1 className={styles.title}>{t('title')}</h1>
          {charger && (
            <p className={styles.subtitle}>{charger.display_name ?? charger.identity}</p>
          )}
        </div>
      </header>

      {(isLoading || leaving) && <div className={styles.skeleton} />}

      {!isLoading && error && (
        <div className={styles.errorBox}>
          {error instanceof Error ? error.message : t('error.load')}
        </div>
      )}

      {charger && gateNote && (
        <div className={styles.note}>
          <p className={styles.noteBody}>{t(`${gateNote}.body`)}</p>
          <button
            type="button"
            className={formStyles.btnSecondary}
            onClick={() => onNavigate(chargerPath)}
          >
            {t(`${gateNote}.back`)}
          </button>
        </div>
      )}

      {charger && home && !gateNote && !leaving && (
        <Walk
          charger={charger}
          chargerId={chargerId}
          onEnrolled={handleEnrolled}
        />
      )}
    </div>
  );
}

/**
 * The walk body. Mounted only once the charger has loaded so every
 * field seeds from a real value exactly once — a later background
 * refetch of the charger must not stomp what the resident is typing.
 */
function Walk({
  charger, chargerId, onEnrolled,
}: {
  charger: MyChargerDetail;
  chargerId: string;
  onEnrolled: () => Promise<void>;
}) {
  const { t } = useTranslation('earn');
  // home_monetization is non-null here — the parent renders Walk only
  // for a home charger — but the type still says nullable.
  const home = charger.home_monetization;

  const [draft] = useState(() => readDraft(chargerId));
  const stored = home?.guest_rate_cents ?? null;
  // "Price step done" = the server has a rate (an enrolled home reached
  // by a stale link) OR the draft holds a valid one.
  const priceDone = stored != null || (draft != null && validGuestCents(draft.guestRateStr) != null);

  const [step, setStep] = useState<Step>(() => {
    const requested = readStepParam();
    // ?step=paid with no price to write would enrol a home that then
    // cannot open to guests; drop back to Price instead.
    if (requested === 'paid' && !priceDone) return 'price';
    if (requested) return requested;
    return priceDone ? 'paid' : 'price';
  });

  const stepIndex = STEPS.indexOf(step);

  return (
    <>
      <ol className={styles.dots} aria-label={t('steps.aria', {
        current: stepIndex + 1, total: STEPS.length, label: t(`steps.${step}`),
      })}
      >
        {STEPS.map((s, i) => (
          <li
            key={s}
            className={`${styles.dot} ${i < stepIndex ? styles.dotDone : ''} ${i === stepIndex ? styles.dotOn : ''}`}
            aria-current={i === stepIndex ? 'step' : undefined}
          >
            <span className={styles.dotMark} aria-hidden>
              {i < stepIndex ? <Check size={12} /> : i + 1}
            </span>
            <span className={styles.dotLabel}>{t(`steps.${s}`)}</span>
          </li>
        ))}
      </ol>

      {step === 'price' && (
        <PriceStep
          chargerId={chargerId}
          home={home}
          draft={draft}
          onDone={() => setStep('hours')}
        />
      )}
      {step === 'hours' && (
        <HoursStep
          chargerId={chargerId}
          saved={charger.guest_time_windows}
          onBack={() => setStep('price')}
          onDone={() => setStep('paid')}
        />
      )}
      {step === 'paid' && (
        <PaidStep
          chargerId={chargerId}
          home={home}
          windowCount={charger.guest_time_windows.length}
          onChangeStep={setStep}
          onEnrolled={onEnrolled}
        />
      )}
    </>
  );
}

// ── Step 1: Price ──────────────────────────────────────────────────────
function PriceStep({
  chargerId, home, draft, onDone,
}: {
  chargerId: string;
  home: MyChargerDetail['home_monetization'];
  draft: EarnDraft | null;
  onDone: () => void;
}) {
  const { t, i18n } = useTranslation('earn');
  // The margin sentence and the field labels are the charger Settings
  // form's, so the walk and the form can never word a price differently.
  const { t: tMy } = useTranslation('myChargers');
  const qc = useQueryClient();
  // Every amount on this step is in the home's currency (item 3): CA$ at a
  // Canadian home. A charger payload without the field reads USD.
  const m = moneyFormat(home?.currency, i18n.language);
  // A home priced before item 3 (an enrolled home reached by a stale
  // link): guests pay the stored price in guest_rate_currency until the
  // walk saves it in the home's currency. Said under the price.
  const storedPriceCurrency = home?.guest_rate_currency ? home.guest_rate_currency.toUpperCase() : null;
  const currencyMove = home?.currency != null && storedPriceCurrency !== null && storedPriceCurrency !== m.currency;
  const prefixClass = styles.pricePrefix;
  // A symbol wider than $ (CA$) needs the input text to start later.
  const inputClass = m.symbol.length > 1 ? `${styles.priceInput} ${styles.priceInputWide}` : styles.priceInput;

  // Draft beats stored: the draft is what the resident typed last.
  const seedDollars = (draftStr: string | undefined, cents: number | null | undefined): string => {
    if (draftStr != null && draftStr !== '') return draftStr;
    return cents != null ? (cents / 100).toFixed(2) : '';
  };
  const [guestRateStr, setGuestRateStr] = useState(
    () => seedDollars(draft?.guestRateStr, home?.guest_rate_cents),
  );
  // EVI's connection fee on this charger, read-only (Tier 2): set by
  // EVI, kept by EVI, charged on top of the guest price once per paid
  // session. Null = none here.
  const connectionFeeCents = home?.connection_fee_cents ?? null;
  const [idleFeeStr, setIdleFeeStr] = useState(
    () => seedDollars(draft?.idleFeeStr, home?.time_rate_cents),
  );
  const [idleLimitStr, setIdleLimitStr] = useState(() => draft?.idleLimitStr ?? (home?.idle_max_charge_cents != null ? String(home.idle_max_charge_cents / 100) : ''));
  // The idle fee's grace (Warren, 2026-09-28: required with every idle fee,
  // 5 to 30 minutes, default 15), prefilled with the default.
  const [idleGraceStr, setIdleGraceStr] = useState(
    () => draft?.idleGraceStr ?? String(home?.idle_grace_minutes ?? IDLE_GRACE_DEFAULT_MINUTES),
  );

  // The electricity estimate. Display only — it feeds the margin line
  // and nothing else. When the owner has none we take it inline here
  // rather than sending them to Home Settings mid-walk; a save lands on
  // the same PATCH /me/home/rate Home Settings uses, then the local
  // copy drives the line immediately while the charger query refetches.
  const [localUtility, setLocalUtility] = useState<UtilityRateEstimate | null>(null);
  const utilityRate: UtilityRateEstimate | null = home?.utility_rate ?? localUtility;
  const [utilityStr, setUtilityStr] = useState('');
  const [utilitySaving, setUtilitySaving] = useState(false);
  const [utilityErr, setUtilityErr] = useState('');

  // The homeowner's rate on the Site electricity constant for a flat rate
  // (Warren, 2026-09-27: 40¢; 60¢ since 21 October 2026, night: "a
  // homeowner's own rate cap go to 60 cents, like the rest"). PATCH
  // /me/home/rate refuses above it too; said inline as it is typed, and
  // Save holds. Kept to a hundredth of a cent ($0.4363 is 43.63¢); blank or
  // zero saves nothing here.
  const typedUtilityRaw = utilityDollarsToCents(utilityStr);
  const typedUtilityCents = typedUtilityRaw != null && typedUtilityRaw > 0 ? typedUtilityRaw : null;
  const utilityOverCeiling = isUtilityRateOverCeiling(typedUtilityCents, 'flat');
  // The second step above 40¢ (Warren, 21 October 2026, night), taken away by any edit to the rate.
  const [utilityConfirmCents, setUtilityConfirmCents] = useState<number | null>(null);
  useEffect(() => { setUtilityConfirmCents(null); }, [utilityStr]);

  const liveGuestCents = parseDollarsToCents(guestRateStr);
  const margin = computeHostMargin(liveGuestCents, utilityRate);
  const marginLine = margin
    ? hostMarginText(margin, tMy, connectionFeeCents, { currency: m.currency, locale: m.locale })
    : null;

  const typedIdleCents = parseDollarsToCents(idleFeeStr);
  // What the host banks on the idle fee — it is not exempt from EVI's 15%.
  const idleKeepCents = typedIdleCents != null ? hostKeepCents(typedIdleCents) : null;
  // Idle bills for at most IDLE_FEE_MAX_MINUTES a session (Warren,
  // 2026-09-27), so the most a guest can pay in idle is rate x that.
  const idleMaxCents = typedIdleCents != null && typedIdleCents > 0
    ? Math.min(typedIdleCents * IDLE_FEE_MAX_MINUTES, parseDollarsToCents(idleLimitStr) ?? Infinity)
    : null;
  const hasTypedIdleFee = typedIdleCents != null && typedIdleCents > 0;

  // useFormError, not useState: a repeat of the same validation message
  // in one tick would otherwise be batched into silence. See FormError.
  const [errorMsg, setErrorMsg] = useFormError();

  const saveUtility = async (confirmed = false): Promise<void> => {
    const cents = typedUtilityCents;
    if (cents == null || utilityOverCeiling) return;
    // This field shows only while the home has no rate, so there is no saved rate to compare: above 40¢ asks first.
    const asked = homeRateConfirmCents({ rate_type: 'flat', rate_cents_per_kwh: cents });
    if (asked !== null && !confirmed) { setUtilityConfirmCents(asked); return; }
    setUtilitySaving(true);
    setUtilityErr('');
    try {
      await setMyHomeRate({ rate_type: 'flat', rate_cents_per_kwh: cents, ...(asked !== null ? { confirm_above_40: true } : {}) });
      setUtilityConfirmCents(null);
      setLocalUtility({ type: 'flat', base_cents: cents, min_cents: cents, max_cents: cents });
      // Home Settings and the charger detail both read this rate.
      void qc.invalidateQueries({ queryKey: ['my-charger', chargerId] });
      void qc.invalidateQueries({ queryKey: ['my-home'] });
      toast(t('price.electricity.saved'), { tone: 'accent' });
    } catch (err) {
      const { message } = await readErrorEnvelope(err);
      setUtilityErr(message ?? t('error.save'));
    } finally {
      setUtilitySaving(false);
    }
  };

  const handleContinue = (e: FormEvent): void => {
    e.preventDefault();
    setErrorMsg('');
    // A price is set in the home's currency (item 3), and the server
    // refuses one for a home whose address names no country. Asked here,
    // before enrolment, so the save at step 3 never strands a
    // half-enrolled home. Null only: an older Api sends no field.
    if (home?.currency === null) {
      setErrorMsg(t('price.validation.countryRequired'));
      return;
    }
    const cents = validGuestCents(guestRateStr);
    if (cents == null) {
      setErrorMsg(tMy('detail.validation.guestPriceRange', {
        min: m.money(GUEST_RATE_MIN_CENTS), max: m.money(GUEST_RATE_MAX_CENTS),
      }));
      return;
    }
    // A typed idle fee that rounds to nothing (0.004) or sits outside
    // the server's bounds must fail HERE: the price is written after
    // enrolment, so a 400 then would strand a half-enrolled home.
    const feeTyped = (s: string): boolean => s.trim() !== '';
    if (feeTyped(idleFeeStr) && (typedIdleCents == null || typedIdleCents < HOME_IDLE_RATE_MIN_CENTS || typedIdleCents > HOME_IDLE_RATE_MAX_CENTS)) {
      setErrorMsg(t('price.validation.idleFeeRange', {
        min: m.money(HOME_IDLE_RATE_MIN_CENTS), max: m.money(HOME_IDLE_RATE_MAX_CENTS),
      }));
      return;
    }
    // An idle fee without a grace is refused (Warren, 2026-09-28), here and
    // not at step 3, for the same stranding reason as the fee's range.
    if (hasTypedIdleFee && (idleGraceStr.trim() === '' || !isValidIdleGraceMinutes(Number(idleGraceStr)))) {
      setErrorMsg(tMy('detail.validation.idleGraceRange', {
        min: IDLE_GRACE_MIN_MINUTES, max: IDLE_GRACE_MAX_MINUTES,
      }));
      return;
    }
    // Held until enrolment — see EarnDraft for why this is not a PATCH.
    if (idleLimitStr.trim() !== '' && (!Number.isFinite(Number(idleLimitStr)) || Number(idleLimitStr) < 0)) {
      setErrorMsg(t('price.validation.idleMaximum')); return;
    }
    writeDraft(chargerId, { guestRateStr, idleFeeStr, idleGraceStr, idleLimitStr });
    onDone();
  };

  return (
    <form className={styles.step} onSubmit={handleContinue} noValidate>
      <p className={styles.intro}>{t('price.intro')}</p>

      {errorMsg && (
        <FormError announce={errorMsg.nonce}>{errorMsg.message}</FormError>
      )}

      <div className={formStyles.field}>
        <label htmlFor="earn-guest-rate" className={formStyles.label}>
          {tMy('detail.guestPrice.label')}
        </label>
        <div className={styles.priceGroup}>
          <span className={prefixClass} aria-hidden>{m.symbol}</span>
          <input
            id="earn-guest-rate"
            className={inputClass}
            type="number"
            inputMode="decimal"
            min={GUEST_RATE_MIN_CENTS / 100}
            max={GUEST_RATE_MAX_CENTS / 100}
            step={0.01}
            // Suggested, not prefilled: the field stays empty so the
            // price is a choice, but the margin line below already
            // answers "what would $0.30 earn me" the moment they type.
            placeholder={(SUGGESTED_GUEST_RATE_CENTS / 100).toFixed(2)}
            value={guestRateStr}
            onChange={(e) => setGuestRateStr(e.target.value)}
            aria-label={tMy('detail.guestPrice.aria')}
          />
          <span className={styles.priceSuffix} aria-hidden>/kWh</span>
        </div>
        {currencyMove && storedPriceCurrency && (
          <p className={formStyles.hint} role="note">
            {tMy('detail.guestPrice.currencyChange', { from: storedPriceCurrency, to: m.currency })}
          </p>
        )}
        {marginLine && (
          <p className={marginLine.loss ? styles.marginWarn : styles.margin}>
            {marginLine.text}
          </p>
        )}
      </div>

      {utilityRate != null && <p className={formStyles.hint}>{t('price.electricity.hint')}</p>}
      {utilityRate == null && (
        <div className={formStyles.field}>
          <label htmlFor="earn-utility-rate" className={formStyles.label}>
            {t('price.electricity.label')}
          </label>
          <div className={styles.utilityRow}>
            <div className={styles.priceGroup}>
              <span className={prefixClass} aria-hidden>{m.symbol}</span>
              <input
                id="earn-utility-rate"
                className={inputClass}
                type="number"
                inputMode="decimal"
                min={0}
                max={utilityCeilingInDollars('flat')}
                step={0.0001}
                placeholder="0.15"
                value={utilityStr}
                onChange={(e) => setUtilityStr(e.target.value)}
                disabled={utilitySaving}
                aria-invalid={utilityOverCeiling || undefined}
                aria-describedby={utilityOverCeiling ? 'earn-utility-ceiling' : undefined}
              />
              <span className={styles.priceSuffix} aria-hidden>/kWh</span>
            </div>
            <button
              type="button"
              className={styles.utilitySave}
              disabled={utilitySaving || typedUtilityCents == null || utilityOverCeiling || utilityConfirmCents !== null}
              onClick={() => { void saveUtility(); }}
            >
              {utilitySaving
                ? <><Loader2 size={13} className={styles.spin} /> {t('saving')}</>
                : t('price.electricity.save')}
            </button>
          </div>
          <p className={formStyles.hint}>{t('price.electricity.hint')}</p>
          {utilityConfirmCents !== null && (
            <HomeRateConfirmStep cents={utilityConfirmCents} currency={m.currency} busy={utilitySaving}
              onConfirm={() => { void saveUtility(true); }} onCancel={() => setUtilityConfirmCents(null)} />
          )}
          {utilityOverCeiling && (
            <p id="earn-utility-ceiling" className={formStyles.errorMsg} role="alert">
              {t('price.electricity.ceiling', { max: utilityCeilingLabel('flat', m.currency, m.locale) })}
            </p>
          )}
          {utilityErr && <p className={formStyles.errorMsg}>{utilityErr}</p>}
        </div>
      )}

      {/* EVI's connection fee (read-only) + the idle fee. The connection
          fee is shown, never typed: EVI sets it per charger and keeps
          it, so it is not the host's to price and not part of their
          85%. The idle fee is the host's and quotes the NET they keep,
          because it is not exempt from EVI's 15%. */}
      <div className={styles.feeGrid}>
        <div className={formStyles.field}>
          <span className={formStyles.label}>
            {tMy('detail.connectionFee.label')}
          </span>
          <p className={styles.feeReadOnly}>
            {connectionFeeCents != null && connectionFeeCents > 0
              ? tMy('detail.connectionFee.value', { fee: m.fee(connectionFeeCents) })
              : tMy('detail.connectionFee.none')}
          </p>
          {connectionFeeCents != null && connectionFeeCents > 0 && (
            <p className={formStyles.hint}>{tMy('detail.connectionFee.hint')}</p>
          )}
        </div>

        <div className={formStyles.field}>
          <label htmlFor="earn-idle-fee" className={formStyles.label}>
            {tMy('detail.idleFee.label')}
          </label>
          <div className={styles.priceGroup}>
            <span className={prefixClass} aria-hidden>{m.symbol}</span>
            <input
              id="earn-idle-fee"
              className={inputClass}
              type="number"
              inputMode="decimal"
              min={0}
              max={HOME_IDLE_RATE_MAX_CENTS / 100}
              step={0.01}
              placeholder="0.00"
              value={idleFeeStr}
              onChange={(e) => setIdleFeeStr(e.target.value)}
            />
            <span className={styles.priceSuffix} aria-hidden>/min</span>
          </div>
          <p className={formStyles.hint}>
            {renderInline(idleText(i18n.language, 'rateHint', { max: idleMoney(HOME_IDLE_RATE_MAX_CENTS, m.currency, i18n.language) }))}
          </p>
          {idleKeepCents != null && (
            <p className={formStyles.hint}>
              {renderInline(idleText(i18n.language, 'keep', { amount: idleMoney(idleKeepCents, m.currency, i18n.language) }))}
            </p>
          )}
          {idleMaxCents != null && (
            <p className={`${formStyles.hint} ${styles.idleMax}`}>
              {renderInline(idleHostSummary(typedIdleCents!, parseDollarsToCents(idleLimitStr), m.currency, i18n.language))}
            </p>
          )}
        </div>

        <div className={formStyles.field}>
          <label htmlFor="earn-idle-limit" className={formStyles.label}>{tMy('detail.idleFee.limitLabel')}</label>
          <div className={styles.priceInputGroup}>
            <span className={styles.pricePrefix} aria-hidden>{m.currency === 'CAD' ? 'CA$' : 'US$'}</span>
            <input id="earn-idle-limit" aria-label={`${tMy('detail.idleFee.limitLabel')} (${m.currency})`} className={`${styles.priceInput} ${styles.priceInputWide}`} type="number" min={0} step={0.01}
              value={idleLimitStr} onChange={e => setIdleLimitStr(e.target.value)} placeholder={tMy('detail.idleFee.limitPlaceholder')} />
          </div>
          <p className={formStyles.hint}>{renderInline(idleText(i18n.language, 'limitHint'))}</p>
        </div>
        <div className={formStyles.field}>
          <label htmlFor="earn-idle-grace" className={formStyles.label}>
            {tMy('detail.idleGrace.label')}
          </label>
          <div className={styles.priceGroup}>
            <input
              id="earn-idle-grace"
              className={styles.priceInput}
              type="number"
              inputMode="numeric"
              min={IDLE_GRACE_MIN_MINUTES}
              max={IDLE_GRACE_MAX_MINUTES}
              step={1}
              value={idleGraceStr}
              onChange={(e) => setIdleGraceStr(e.target.value)}
              disabled={!hasTypedIdleFee}
            />
            <span className={styles.priceSuffix} aria-hidden>{tMy('detail.idleGrace.suffix')}</span>
          </div>
          <p className={formStyles.hint}>
            {renderInline(idleText(i18n.language, 'graceHint'))}
          </p>
        </div>
      </div>

      <div className={styles.actions}>
        <button type="submit" className={formStyles.btnPrimary}>
          {t('price.continue')}
        </button>
      </div>
    </form>
  );
}

// ── Step 2: Hours ──────────────────────────────────────────────────────
function HoursStep({
  chargerId, saved, onBack, onDone,
}: {
  chargerId: string;
  saved: MyChargerDetail['guest_time_windows'];
  onBack: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation('earn');
  const { t: tMy } = useTranslation('myChargers');
  const qc = useQueryClient();
  const [windows, setWindows] = useState<GuestTimeWindowState[]>(() => seedWindows(saved));
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useFormError();

  const handleContinue = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setErrorMsg('');
    // A window with no day picked would never match; the server rejects
    // it too, but naming the row here beats a generic 400.
    const dayless = windows.findIndex((w) => w.days_of_week.length === 0);
    if (dayless >= 0) {
      setErrorMsg(tMy('detail.validation.windowNeedsDay', { index: dayless + 1 }));
      return;
    }
    setSaving(true);
    try {
      // Only the three wire fields — the client key never leaves the tab.
      await updateMyCharger(chargerId, {
        guest_time_windows: windows.map(({ start_hour, end_hour, days_of_week }) => ({
          start_hour, end_hour, days_of_week,
        })),
      });
      void qc.invalidateQueries({ queryKey: ['my-charger', chargerId] });
      onDone();
    } catch (err) {
      const { message } = await readErrorEnvelope(err);
      setErrorMsg(message ?? t('error.saveHours'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className={styles.step} onSubmit={(e) => { void handleContinue(e); }} noValidate>
      <p className={styles.intro}>{t('hours.intro')}</p>

      {errorMsg && (
        <FormError announce={errorMsg.nonce}>{errorMsg.message}</FormError>
      )}

      <TimeWindowRows<GuestTimeWindowState>
        windows={windows}
        onChange={setWindows}
        getDays={(w) => w.days_of_week}
        setDays={(w, days) => ({ ...w, days_of_week: days })}
        getStartHour={(w) => w.start_hour}
        setStartHour={(w, h) => ({ ...w, start_hour: h })}
        getEndHour={(w) => w.end_hour}
        setEndHour={(w, h) => ({ ...w, end_hour: h })}
        // 9–17 default mirrors the charger Settings form: standard
        // hours, empty days so the resident picks at least one.
        createEmpty={() => ({
          days_of_week: [], start_hour: 9, end_hour: 17, _key: nextWindowKey(),
        })}
        getKey={(w) => w._key}
        emptyStateText={tMy('detail.windows.empty')}
        disabled={saving}
      />

      <div className={styles.actions}>
        <button type="submit" className={formStyles.btnPrimary} disabled={saving}>
          {saving
            ? <><Loader2 size={14} className={styles.spin} /> {t('saving')}</>
            : t('hours.continue')}
        </button>
        <button type="button" className={formStyles.btnSecondary} onClick={onBack} disabled={saving}>
          {t('stepBack')}
        </button>
      </div>
    </form>
  );
}

// ── Step 3: Get paid ───────────────────────────────────────────────────
function PaidStep({
  chargerId, home, windowCount, onChangeStep, onEnrolled,
}: {
  chargerId: string;
  home: MyChargerDetail['home_monetization'];
  windowCount: number;
  onChangeStep: (step: Step) => void;
  onEnrolled: () => Promise<void>;
}) {
  const { t, i18n } = useTranslation('earn');
  const { t: tConnect } = useTranslation('connect');
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  // What "Turn on earning" will commit — the draft when there is one,
  // else what the server already holds. Shown, with a way back to each
  // step, so the price is never enrolled unseen.
  const draft = readDraft(chargerId);
  // The summary quotes the price in its currency (item 3): a draft is
  // saved in the home's; a stored price is in the one guests pay it in
  // today (guest_rate_currency), which at a home priced before item 3 is
  // not yet the home's.
  const m = moneyFormat(
    draft ? home?.currency : (home?.guest_rate_currency ?? home?.currency),
    i18n.language,
  );
  const summaryRate = draft ? validGuestCents(draft.guestRateStr) : (home?.guest_rate_cents ?? null);
  // EVI's fee, from the charger: never from the draft, never the host's.
  const summaryConnectionFee = home?.connection_fee_cents ?? null;
  const summaryIdle = draft ? parseDollarsToCents(draft.idleFeeStr) : (home?.time_rate_cents ?? null);
  const summaryGrace = draft?.idleGraceStr !== undefined && draft.idleGraceStr.trim() !== ''
    ? Number(draft.idleGraceStr)
    : (home?.idle_grace_minutes ?? IDLE_GRACE_DEFAULT_MINUTES);
  const summaryMaximum = draft ? parseDollarsToCents(draft.idleLimitStr ?? '') : home?.idle_max_charge_cents;

  const connectQ = useQuery({
    queryKey: ['connect-status'],
    queryFn: fetchConnectStatus,
    // Back from Stripe (same tab, or the external browser on iOS) the
    // status is the first thing to re-read.
    refetchOnWindowFocus: true,
    // Poll while Stripe is verifying; every other state changes only
    // when the resident does something. Same rule the card renders, so
    // the spinner and the polling can never disagree. And slowly while
    // an old Api still answers a host with no account (apiTakesCountry):
    // the Stripe button waits for the new one.
    refetchInterval: (query) => {
      const d = query.state.data;
      if (!d) return false;
      if (connectCardState(d) === 'confirming') return 10_000;
      return !d.has_account && !apiTakesCountry(d) ? 30_000 : false;
    },
    refetchIntervalInBackground: false,
  });
  const cardState = connectQ.data ? connectCardState(connectQ.data) : null;
  const ready = cardState === 'ready';

  // "Name as on your ID" — prefills the Express legal name at account
  // creation, which is what Stripe verifies the document against.
  const [legalName, setLegalName] = useState(user?.name ?? '');

  // The bank account country for a NEW account (item 2): nothing is
  // preselected, and the Stripe button waits for a chosen and confirmed
  // country. An existing account keeps its own; the card shows it.
  const [payout, setPayout] = useState<{ country: PayoutCountry | ''; confirmed: boolean }>({
    country: '', confirmed: false,
  });
  const hasAccount = connectQ.data?.has_account === true;
  // An Api from before the country choice would ignore it and make the
  // account Canadian for good: no create until the new one answers.
  const takesCountry = apiTakesCountry(connectQ.data);
  const mayOpenStripe = canStartPayout({
    hasAccount, country: payout.country, confirmed: payout.confirmed, apiTakesCountry: takesCountry,
  });

  // Opening Stripe. Disabled on tap, released when the page regains
  // visibility (the resident came back) or after five minutes,
  // whichever first — never left dead behind a link that already
  // opened in another browser.
  const [opening, setOpening] = useState(false);
  useEffect(() => {
    if (!opening) return;
    const release = (): void => {
      if (document.visibilityState === 'visible') setOpening(false);
    };
    document.addEventListener('visibilitychange', release);
    const timer = window.setTimeout(() => setOpening(false), STRIPE_BUTTON_RELEASE_MS);
    return () => {
      document.removeEventListener('visibilitychange', release);
      window.clearTimeout(timer);
    };
  }, [opening]);

  const [errorMsg, setErrorMsg] = useFormError();
  const [enrolling, setEnrolling] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  const openStripe = async (): Promise<void> => {
    setOpening(true);
    setErrorMsg('');
    const chosen = !hasAccount && payout.country ? payout.country : null;
    try {
      const name = legalName.trim();
      const result = await startNewPayoutAccount({
        // The route wants 2..120 chars; anything shorter is no prefill.
        ...(name.length >= 2 ? { legalName: name.slice(0, 120) } : {}),
        next: `/my-chargers/${chargerId}/earn`,
        // Only for a new account; the server ignores it otherwise.
        ...(chosen ? { country: chosen } : {}),
      });
      if (result.kind === 'otherCountry' && chosen) {
        // The account came back in the other country (an old Api task
        // answered): stop before Stripe's checks, and show the account.
        const actual = payoutCountryFrom(result.country);
        void connectQ.refetch();
        setErrorMsg(tConnect('country.createdOther', {
          account: actual ? tConnect(payoutCountryTextKey(actual)) : result.country,
          country: tConnect(payoutCountryTextKey(chosen)),
        }));
        setOpening(false);
        return;
      }
      if (result.kind === 'url') window.location.href = result.url;
    } catch (err) {
      const { code, message } = await readErrorEnvelope(err);
      // 400 CONNECT_COUNTRY_REQUIRED: the status said an account exists
      // but the server has none. Re-read it so the country choice shows.
      if (isCountryRequiredCode(code)) {
        void connectQ.refetch();
        setErrorMsg(tConnect('country.required'));
      } else {
        setErrorMsg(message ?? t('error.openStripe'));
      }
      setOpening(false);
    }
  };

  /**
   * Enrol, then write the held price. Enrolment is idempotent
   * server-side (re-stamps the same account), so a failed price write
   * is safe to retry from the same button.
   */
  const finish = async (): Promise<void> => {
    await enrollHomeMonetization();
    const draft = readDraft(chargerId);
    const cents = draft ? validGuestCents(draft.guestRateStr) : null;
    // No draft (storage cleared between the walk and Stripe): the
    // enrolment stands, and the detail page asks for a price before the
    // charger can open to guests. A draft from an EARLIER visit only
    // writes when the server still has no price — it must never
    // overwrite one set since, from the charger Settings form.
    const serverHasPrice = home?.guest_rate_cents != null;
    if (cents != null && (draftIsFresh(chargerId) || !serverHasPrice)) {
      // Blank idle fee = no idle fee: null clears on the wire, and the
      // draft is the whole of what the resident typed. No connection
      // fee is ever sent: it is EVI's.
      const idleCents = parseDollarsToCents(draft?.idleFeeStr ?? '');
      const grace = Number(draft?.idleGraceStr ?? IDLE_GRACE_DEFAULT_MINUTES);
      await updateHomeGuestRate(cents, {
        timeRateCents: idleCents,
        idleMaxChargeCents: parseDollarsToCents(draft?.idleLimitStr ?? ''),
        // Checked at step 2; a draft from before the field sends none and
        // the server keeps the stored grace or the default.
        ...(idleCents != null && idleCents > 0 && isValidIdleGraceMinutes(grace)
          ? { idleGraceMinutes: grace }
          : {}),
      });
    }
    clearDraft(chargerId);
    // The parent invalidates, toasts and navigates — it owns the query
    // and must not swap this step for the "already earning" note while
    // the refetch is in flight.
    await onEnrolled();
  };

  /** `afterAck` — this is the one retry the disclaimer ack gets. A
   *  second 412 on that pass means the ack did not land, and reopening
   *  the modal would loop; it reads as the generic enrol error. */
  const turnOn = async (afterAck = false): Promise<void> => {
    setEnrolling(true);
    setErrorMsg('');
    try {
      await finish();
    } catch (err) {
      const { code, message } = await readErrorEnvelope(err);
      // 412 — first time hosting: the disclaimer opens here, and the
      // ack retries the whole sequence once.
      if (code === 'DISCLAIMER_REQUIRED' && !afterAck) {
        setShowDisclaimer(true);
        return;
      }
      // 402 — Stripe flipped under us (or the status was stale). The
      // card re-reads and the Stripe button is the way forward.
      if (code === 'EXPRESS_ONBOARDING_REQUIRED') {
        void connectQ.refetch();
        setErrorMsg(message ?? t('paid.backToStripe'));
        return;
      }
      // 409 PAYOUT_TERMS_REQUIRED never reaches here — the api helper
      // raises the accept card and retries; a decline propagates as-is.
      setErrorMsg(message ?? t('error.enroll'));
    } finally {
      setEnrolling(false);
    }
  };

  return (
    <div className={styles.step}>
      {errorMsg && (
        <FormError announce={errorMsg.nonce}>{errorMsg.message}</FormError>
      )}

      <dl className={styles.summary} aria-label={t('summary.aria')}>
        <div className={styles.summaryRow}>
          <dt className={styles.summaryKey}>{t('summary.price')}</dt>
          <dd className={styles.summaryVal}>
            {summaryRate != null ? `${m.money(summaryRate)}/kWh` : t('summary.notSet')}
            {summaryConnectionFee != null && summaryConnectionFee > 0
              && ` · ${t('summary.connectionFee', { fee: m.fee(summaryConnectionFee) })}`}
            {summaryIdle != null && summaryIdle > 0
              && <> · {renderInline(idleDisclosure({ time_rate: summaryIdle, idle_grace_minutes: summaryGrace,
                idle_max_charge_cents: summaryMaximum }, m.currency, i18n.language, i18n.getFixedT(i18n.language, 'preSession')))}</>}
          </dd>
          <button type="button" className={styles.summaryEdit} onClick={() => onChangeStep('price')} disabled={enrolling}>
            {t('summary.change')}
          </button>
        </div>
        <div className={styles.summaryRow}>
          <dt className={styles.summaryKey}>{t('summary.hours')}</dt>
          <dd className={styles.summaryVal}>
            {windowCount === 0 ? t('summary.anyTime') : t('summary.windows', { count: windowCount })}
          </dd>
          <button type="button" className={styles.summaryEdit} onClick={() => onChangeStep('hours')} disabled={enrolling}>
            {t('summary.change')}
          </button>
        </div>
      </dl>

      {connectQ.error && !connectQ.data ? (
        // The status read failed (network, 5xx): say so and offer a
        // retry instead of an endless "Checking with Stripe…".
        <div className={styles.note} role="alert">
          <p className={styles.noteBody}>{t('error.status')}</p>
          <button
            type="button"
            className={formStyles.btnSecondary}
            onClick={() => { void connectQ.refetch(); }}
            disabled={connectQ.isFetching}
          >
            <RefreshCw size={14} /> {t('retry')}
          </button>
        </div>
      ) : (
        <ConnectStatusCard status={connectQ.data} loading={connectQ.isLoading} />
      )}

      {!ready && cardState !== null && (
        <>
          {/* The legal name only prefills at account CREATION (the
              route ignores it for an existing account), so the field
              and the "what Stripe asks for" line show only before the
              first pass. Later passes are Stripe's own form re-asking
              for what the card already names. */}
          {cardState === 'notStarted' && (
            <>
              <p className={styles.intro}>{t('paid.intro')}</p>
              <p className={styles.intro}>{t('paid.guestsWait')}</p>
              <div className={formStyles.field}>
                <label htmlFor="earn-legal-name" className={formStyles.label}>
                  {t('paid.legalName.label')}
                </label>
                <input
                  id="earn-legal-name"
                  className={formStyles.input}
                  type="text"
                  autoComplete="name"
                  maxLength={120}
                  placeholder={t('paid.legalName.placeholder')}
                  value={legalName}
                  onChange={(e) => setLegalName(e.target.value)}
                  disabled={opening}
                />
              </div>
              <PayoutCountryField
                idPrefix="earn"
                country={payout.country}
                confirmed={payout.confirmed}
                onChange={setPayout}
                homeCountry={payoutCountryFrom(connectQ.data?.home_country ?? null)}
                updating={!takesCountry}
                disabled={opening}
              />
            </>
          )}
          {/* Confirming is Stripe's turn, not the resident's: no button
              while the card is polling. Needed / error / not started
              all go back into Stripe's form. */}
          {cardState !== 'confirming' && (
            <div className={styles.actions}>
              <button
                type="button"
                className={formStyles.btnPrimary}
                disabled={opening || !mayOpenStripe}
                onClick={() => { void openStripe(); }}
              >
                {opening
                  ? <><Loader2 size={14} className={styles.spin} /> {t('paid.opening')}</>
                  : <><ExternalLink size={14} /> {t('paid.openStripe')}</>}
              </button>
            </div>
          )}
        </>
      )}

      {ready && (
        <div className={styles.actions}>
          <button
            type="button"
            className={formStyles.btnPrimary}
            disabled={enrolling}
            onClick={() => { void turnOn(); }}
          >
            {enrolling
              ? <><Loader2 size={14} className={styles.spin} /> {t('paid.turningOn')}</>
              : t('paid.turnOn')}
          </button>
        </div>
      )}

      {showDisclaimer && (
        <DisclaimerModal
          title={t('disclaimer.title')}
          body={
            // Twin disclaimer copy — owner-side, identical to the charger
            // Settings form's HomeEnrollSection. Recipient-side
            // counterpart lives in MyAccessPage. If any one edits, the
            // others should match.
            <>
              EVI facilitates this connection. The agreement (times,
              pricing, expectations) is between you and the other
              driver. We process payments and surface contact info.
              We don&rsquo;t enforce or arbitrate.
            </>
          }
          onAck={async () => {
            await ackResidentDisclaimer();
            void qc.invalidateQueries({ queryKey: ['account'] });
            setShowDisclaimer(false);
            await turnOn(true);
          }}
          onCancel={() => {
            setShowDisclaimer(false);
            setErrorMsg(t('error.disclaimerCanceled'));
          }}
        />
      )}
    </div>
  );
}

/** Pull the structured error envelope out of a ky HTTPError. Both
 *  fields null when the error is not shaped like that (network drop,
 *  JSON parse failure). The MESSAGE matters as much as the code: an
 *  unrecognised code must surface the server's own explanation, never
 *  ky's status line. Same shape as the charger Settings form's helper,
 *  kept local so this page does not import from another page. */
async function readErrorEnvelope(err: unknown): Promise<{ code: string | null; message: string | null }> {
  const response = (err as { response?: Response }).response;
  if (!response) return { code: null, message: null };
  try {
    const body = await response.clone().json() as { error?: { code?: string; message?: string } };
    return { code: body.error?.code ?? null, message: body.error?.message ?? null };
  } catch {
    return { code: null, message: null };
  }
}
